import useSWRImmutable from "swr/immutable";
import useSWR from "swr";
import { useEffect } from "react";
import { useCosmosClient } from "@/services/cosmos";
import {
  type CheckoutSession,
  CheckoutStatus,
  SessionError,
} from "@/types";
import { CksCheckoutStateSchema } from "@/services/cosmos/schema/checkout-service";
import { emptySession } from "@/lib/checkout";
import { logError, logInfo } from "@/services/logging/logger";
import { applyLocaleHandoff } from "@/i18n";
import { syncCheckoutHandoffCookies } from "@/services/cookie/checkout-handoff";

let hasAppliedLocaleFromSession = false;

function applyLocaleFromSession(
  sessionLocale: string | null | undefined,
): void {
  if (hasAppliedLocaleFromSession) return;
  applyLocaleHandoff(sessionLocale);
  hasAppliedLocaleFromSession = true;
}

function logDeclinedOffers(session: CheckoutSession): void {
  const declinedOffers = session.declinedOffers ?? [];
  const declinedWithSubvention = declinedOffers.filter(
    (d) => d.subvention != null && typeof d.subvention === "object",
  );

  if (declinedOffers.length) {
    logInfo("checkout-session", "Declined offers received from session", {
      count: declinedOffers.length,
      withSubventionCount: declinedWithSubvention.length,
      tenures: declinedOffers.map((d) => d.tenure).filter(Boolean),
    });
  }

  if (declinedWithSubvention.length) {
    logInfo("checkout-session", "Declined offers with subvention object", {
      count: declinedWithSubvention.length,
      tenures: declinedWithSubvention.map((d) => d.tenure).filter(Boolean),
      offerIds: declinedWithSubvention.map((d) => d.offerId).filter(Boolean),
    });
  }
}

interface UseCheckoutSessionOptions {
  skip?: boolean;
}

interface CheckoutSessionResult {
  data: CheckoutSession | undefined;
  mutate: (
    data?: CheckoutSession | Promise<CheckoutSession>,
    opts?: { revalidate?: boolean },
  ) => Promise<CheckoutSession | undefined>;
  isLoading: boolean;
  error: Error | undefined;
}

/**
 * Primary hook for fetching and caching the checkout session (v3).
 *
 * Behavioral contract (must not change):
 * - Fetches via SWR immutable with key "session"
 * - On 404: returns empty session with SessionError.NOT_FOUND
 * - On other HTTP errors: returns empty session with SessionError.UNKNOWN
 * - On non-HTTP errors: throws (SWR retry disabled)
 * - Applies locale from session exactly once per app lifecycle
 * - When status is EVALUATING_ORDER_CREDIT, a secondary SWR refetch runs
 * - Sets logger context (checkout_id, order_id) when session loads
 */
export function useCheckoutSessionV3(
  { skip = false }: UseCheckoutSessionOptions = {},
): CheckoutSessionResult {
  const cosmosClient = useCosmosClient();

  const {
    data: session,
    mutate,
    ...rest
  } = useSWRImmutable(
    skip ? null : "session",
    async (): Promise<CheckoutSession> => {
      try {
        const parsed = CksCheckoutStateSchema.parse(
          await cosmosClient.getCheckoutSessionV3(),
        );

        logDeclinedOffers(parsed);
        applyLocaleFromSession(parsed.locale);

        return parsed;
      } catch (err: unknown) {
        const errorResponseStatus =
          (err as { response?: { status?: number } })?.response?.status ?? -1;

        if (errorResponseStatus === 404) {
          return {
            ...emptySession,
            error: { type: SessionError.NOT_FOUND, message: "" },
          };
        }

        if (errorResponseStatus > 0) {
          return {
            ...emptySession,
            error: { type: SessionError.UNKNOWN, message: "" },
          };
        }

        throw err;
      }
    },
    {
      onError: (error: unknown) => {
        logError(
          "checkout-session",
          "Error fetching checkout session",
          error instanceof Error ? error : new Error(String(error)),
          { error },
        );
      },
      shouldRetryOnError: false,
    },
  );

  const shouldPollSession =
    session?.status === CheckoutStatus.EVALUATING_ORDER_CREDIT ||
    session?.status === CheckoutStatus.CHECKING_QUESTIONNAIRE_REQUIRED ||
    session?.status === CheckoutStatus.CHECKING_IDV_ELIGIBILITY ||
    session?.status === CheckoutStatus.VERIFYING_IDV_RESULT;

  // Secondary SWR: refetch while backend is evaluating credit or external verification gates
  useSWR(
    () =>
      !skip && shouldPollSession ? "session/transient_checkout_status" : null,
    async (): Promise<CheckoutSession | undefined> => {
      const freshSession = CksCheckoutStateSchema.parse(
        await cosmosClient.getCheckoutSessionV3(),
      );
      return mutate(freshSession, { revalidate: false });
    },
    {
      onError: (error: unknown) => {
        logError(
          "checkout-session",
          "Error evaluating order credit",
          error instanceof Error ? error : new Error(String(error)),
          { error },
        );
      },
    },
  );

  // Sync logger context with session IDs
  useEffect(() => {
    if (session?.id && session?.order?.id && window.logger) {
      window.logger.setContext("checkout_id", session.id);
      window.logger.setContext("order_id", session.order.id);
    }
  }, [session?.id, session?.order?.id]);

  useEffect(() => {
    if (!session?.customerId) {
      return;
    }

    // Auth cookies only — locale is owned by i18n / URL handoff; do not pass
    // session.locale here or it overwrites ?locale= from the checkout entry URL.
    syncCheckoutHandoffCookies({
      customerId: session.customerId,
    });
  }, [session?.customerId]);

  return { data: session, mutate, ...rest };
}
